/* eslint-disable max-lines */
import React, { useState, useEffect } from 'react';
import { Form, Button, Spinner, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { toast } from 'react-toastify';

import NC4StepOne from './NC4StepOne';
import NC4StepTwo from './NC4StepTwo';
import NC4StepThree from './NC4StepThree';
import NC4StepFour from './NC4StepFour';

import StateService from '../../../../services/State';
import HireDocuments from '../../../../services/HireDocuments';
import moment from 'moment';

interface Schedule2RowDataInternal {
  numChildren: string;
  estimatedDeduction: string;
}

export interface NC4FormData {
  totalAllowances: string;
  additionalAmount: string;
  socialSecurityNumber: string;
  filingStatus: 'single_married_separately' | 'head_of_household' | 'married_jointly_surviving_spouse' | '';
  firstName: string;
  middleInitial: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  employeeSignature: string;
  signatureDate: string;

  single_itemizedDeductionsExceed: 'yes' | 'no' | '';
  single_childDeductionExceed: 'yes' | 'no' | '';
  single_federalAdjustments: 'yes' | 'no' | '';
  single_claimTaxCredits: 'yes' | 'no' | '';
  joint_itemizedDeductionsExceed: 'yes' | 'no' | '';
  joint_childDeductionExceed: 'yes' | 'no' | '';
  joint_federalAdjustments: 'yes' | 'no' | '';
  joint_claimTaxCredits: 'yes' | 'no' | '';
  joint_spouseBenefits: 'yes' | 'no' | '';
  separate_itemizedDeductionsExceed: 'yes' | 'no' | '';
  separate_childDeductionExceed: 'yes' | 'no' | '';
  separate_federalAdjustments: 'yes' | 'no' | '';
  separate_claimTaxCredits: 'yes' | 'no' | '';
  hoh_itemizedDeductionsExceed: 'yes' | 'no' | '';
  hoh_childDeductionExceed: 'yes' | 'no' | '';
  hoh_federalAdjustments: 'yes' | 'no' | '';
  hoh_claimTaxCredits: 'yes' | 'no' | '';

  surviving_itemizedDeductionsExceed: 'yes' | 'no' | '';
  surviving_childDeductionExceed: 'yes' | 'no' | '';
  surviving_federalAdjustments: 'yes' | 'no' | '';
  surviving_claimTaxCredits: 'yes' | 'no' | '';
  part2_line1_itemizedDeductions: string;
  part2_line2_standardDeduction: string;
  part2_line3_subtractLine2FromLine1: string;
  part2_line4_childDeductionAmount: string;
  part2_line5_federalAdjustmentsToIncome: string;
  part2_line6_addLines345: string;
  part2_line7_nonwageIncome: string;
  part2_line8_ncAdditionsToAgi: string;
  part2_line9_addLines78: string;
  part2_line10_subtractLine9FromLine6: string;
  part2_line11_divideLine10By2500: string;
  part2_line12_estimatedNcTaxCredits: string;
  part2_line13_divideLine12By109: string;
  part2_line14_additionalAllowances: string;
  part2_line15_totalAllowancesPartII: string;
  part2_line16_spouseAllowancesToClaim: string;
  part2_line17_finalTotalAllowances: string;

  sch1_homeMortgageInterest: string;
  sch1_realEstatePropertyTaxes: string;
  sch1_charitableContributions: string;
  sch1_medicalAndDentalExpenses: string;
  sch1_repaymentOfClaimOfRightIncome: string;
  sch1_totalEstimatedItemizedDeductions: string;

  sch2_single_income_0_20000: Schedule2RowDataInternal;
  sch2_single_income_20001_30000: Schedule2RowDataInternal;
  sch2_single_income_30001_40000: Schedule2RowDataInternal;
  sch2_single_income_40001_50000: Schedule2RowDataInternal;
  sch2_single_income_50001_60000: Schedule2RowDataInternal;
  sch2_single_income_60001_70000: Schedule2RowDataInternal;
  sch2_single_income_over_70000: Schedule2RowDataInternal;
  sch2_mfj_ss_income_0_40000: Schedule2RowDataInternal;
  sch2_mfj_ss_income_40001_60000: Schedule2RowDataInternal;
  sch2_mfj_ss_income_60001_80000: Schedule2RowDataInternal;
  sch2_mfj_ss_income_80001_100000: Schedule2RowDataInternal;
  sch2_mfj_ss_income_100001_120000: Schedule2RowDataInternal;
  sch2_mfj_ss_income_120001_140000: Schedule2RowDataInternal;
  sch2_mfj_ss_income_over_140000: Schedule2RowDataInternal;
  sch2_hoh_income_0_30000: Schedule2RowDataInternal;
  sch2_hoh_income_30001_45000: Schedule2RowDataInternal;
  sch2_hoh_income_45001_60000: Schedule2RowDataInternal;
  sch2_hoh_income_60001_75000: Schedule2RowDataInternal;
  sch2_hoh_income_75001_90000: Schedule2RowDataInternal;
  sch2_hoh_income_90001_105000: Schedule2RowDataInternal;
  sch2_hoh_income_over_105000: Schedule2RowDataInternal;
  sch2_mfs_income_0_20000: Schedule2RowDataInternal;
  sch2_mfs_income_20001_30000: Schedule2RowDataInternal;
  sch2_mfs_income_30001_40000: Schedule2RowDataInternal;
  sch2_mfs_income_40001_50000: Schedule2RowDataInternal;
  sch2_mfs_income_50001_60000: Schedule2RowDataInternal;
  sch2_mfs_income_60001_70000: Schedule2RowDataInternal;
  sch2_mfs_income_over_70000: Schedule2RowDataInternal;
  sch2_totalEstimatedChildDeduction: string;

  [key: string]: any;
}

interface NC4FormModalProps {
  isOpen: boolean;
  currentUser: any;
  toggle: () => void;
  token: string;
  onSubmitForm: (data: NC4FormData) => void;
  initialData?: Partial<NC4FormData>;
}

const createDefaultSchedule2Row = (): Schedule2RowDataInternal => ({
  numChildren: '',
  estimatedDeduction: '',
});

const NC4FormModal: React.FC<NC4FormModalProps> = ({
  isOpen,
  currentUser,
  toggle,
  token,
  onSubmitForm,
  initialData = {},
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<NC4FormData>({
    totalAllowances: '0',
    additionalAmount: '',
    socialSecurityNumber: '',
    filingStatus: '',
    firstName: '',
    middleInitial: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    employeeSignature: '',
    signatureDate: '',
    single_itemizedDeductionsExceed: '',
    single_childDeductionExceed: '',
    single_federalAdjustments: '',
    single_claimTaxCredits: '',
    joint_itemizedDeductionsExceed: '',
    joint_childDeductionExceed: '',
    joint_federalAdjustments: '',
    joint_claimTaxCredits: '',
    joint_spouseBenefits: '',
    separate_itemizedDeductionsExceed: '',
    separate_childDeductionExceed: '',
    separate_federalAdjustments: '',
    separate_claimTaxCredits: '',
    hoh_itemizedDeductionsExceed: '',
    hoh_childDeductionExceed: '',
    hoh_federalAdjustments: '',
    hoh_claimTaxCredits: '',
    surviving_itemizedDeductionsExceed: '',
    surviving_childDeductionExceed: '',
    surviving_federalAdjustments: '',
    surviving_claimTaxCredits: '',
    part2_line1_itemizedDeductions: '',
    part2_line2_standardDeduction: '',
    part2_line3_subtractLine2FromLine1: '',
    part2_line4_childDeductionAmount: '',
    part2_line5_federalAdjustmentsToIncome: '',
    part2_line6_addLines345: '',
    part2_line7_nonwageIncome: '',
    part2_line8_ncAdditionsToAgi: '',
    part2_line9_addLines78: '',
    part2_line10_subtractLine9FromLine6: '',
    part2_line11_divideLine10By2500: '',
    part2_line12_estimatedNcTaxCredits: '',
    part2_line13_divideLine12By109: '',
    part2_line14_additionalAllowances: '',
    part2_line15_totalAllowancesPartII: '',
    part2_line16_spouseAllowancesToClaim: '',
    part2_line17_finalTotalAllowances: '',
    sch1_homeMortgageInterest: '',
    sch1_realEstatePropertyTaxes: '',
    sch1_charitableContributions: '',
    sch1_medicalAndDentalExpenses: '',
    sch1_repaymentOfClaimOfRightIncome: '',
    sch1_totalEstimatedItemizedDeductions: '',
    sch2_single_income_0_20000: createDefaultSchedule2Row(),
    sch2_single_income_20001_30000: createDefaultSchedule2Row(),
    sch2_single_income_30001_40000: createDefaultSchedule2Row(),
    sch2_single_income_40001_50000: createDefaultSchedule2Row(),
    sch2_single_income_50001_60000: createDefaultSchedule2Row(),
    sch2_single_income_60001_70000: createDefaultSchedule2Row(),
    sch2_single_income_over_70000: createDefaultSchedule2Row(),
    sch2_mfj_ss_income_0_40000: createDefaultSchedule2Row(),
    sch2_mfj_ss_income_40001_60000: createDefaultSchedule2Row(),
    sch2_mfj_ss_income_60001_80000: createDefaultSchedule2Row(),
    sch2_mfj_ss_income_80001_100000: createDefaultSchedule2Row(),
    sch2_mfj_ss_income_100001_120000: createDefaultSchedule2Row(),
    sch2_mfj_ss_income_120001_140000: createDefaultSchedule2Row(),
    sch2_mfj_ss_income_over_140000: createDefaultSchedule2Row(),
    sch2_hoh_income_0_30000: createDefaultSchedule2Row(),
    sch2_hoh_income_30001_45000: createDefaultSchedule2Row(),
    sch2_hoh_income_45001_60000: createDefaultSchedule2Row(),
    sch2_hoh_income_60001_75000: createDefaultSchedule2Row(),
    sch2_hoh_income_75001_90000: createDefaultSchedule2Row(),
    sch2_hoh_income_90001_105000: createDefaultSchedule2Row(),
    sch2_hoh_income_over_105000: createDefaultSchedule2Row(),
    sch2_mfs_income_0_20000: createDefaultSchedule2Row(),
    sch2_mfs_income_20001_30000: createDefaultSchedule2Row(),
    sch2_mfs_income_30001_40000: createDefaultSchedule2Row(),
    sch2_mfs_income_40001_50000: createDefaultSchedule2Row(),
    sch2_mfs_income_50001_60000: createDefaultSchedule2Row(),
    sch2_mfs_income_60001_70000: createDefaultSchedule2Row(),
    sch2_mfs_income_over_70000: createDefaultSchedule2Row(),
    sch2_totalEstimatedChildDeduction: '',
    ...initialData,
  });

  const [states, setStates] = useState<{ id: string; name: string; uf: string }[]>([]);
  const [loadingStates, setLoadingStates] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [signature, setSignature] = useState<any>('');

  useEffect(() => {
    const listStates = async () => {
      if (!token || !isOpen) return;
      setLoadingStates(true);
      try {
        const result = await StateService(token).listSelect();
        setStates(result.data || []);
        if (currentUser?.user?.addresses?.[0]?.stateId) {
          const state = result.data.find((s: { id: string }) => s.id === currentUser.user.addresses[0].stateId);
          if (state) {
            setFormData((prev) => ({ ...prev, state: state.uf }));
          }
        }
      } catch (e: any) {
        toast.error(e?.response?.data?.message || 'Error fetching states');
        setStates([]);
      } finally {
        setLoadingStates(false);
      }
    };

    if (isOpen) {
      setCurrentStep(1);
      setSignature('');
      const defaultRowData = createDefaultSchedule2Row();
      setFormData({
        totalAllowances: '0',
        additionalAmount: '',
        socialSecurityNumber: '',
        filingStatus: '',
        firstName: currentUser?.user?.firstName || '',
        middleInitial: '',
        lastName: currentUser?.user?.lastName || '',
        address: currentUser?.user?.addresses?.[0]?.address || '',
        city: currentUser?.user?.addresses?.[0]?.city || '',
        state: currentUser?.user?.addresses?.[0]?.stateId || '',
        zipCode: currentUser?.user?.addresses?.[0]?.zipCode || '',
        country: currentUser?.user?.addresses?.[0]?.country || '',
        employeeSignature: '',
        signatureDate: '',
        single_itemizedDeductionsExceed: '',
        single_childDeductionExceed: '',
        single_federalAdjustments: '',
        single_claimTaxCredits: '',
        joint_itemizedDeductionsExceed: '',
        joint_childDeductionExceed: '',
        joint_federalAdjustments: '',
        joint_claimTaxCredits: '',
        joint_spouseBenefits: '',
        separate_itemizedDeductionsExceed: '',
        separate_childDeductionExceed: '',
        separate_federalAdjustments: '',
        separate_claimTaxCredits: '',
        hoh_itemizedDeductionsExceed: '',
        hoh_childDeductionExceed: '',
        hoh_federalAdjustments: '',
        hoh_claimTaxCredits: '',
        surviving_itemizedDeductionsExceed: '',
        surviving_childDeductionExceed: '',
        surviving_federalAdjustments: '',
        surviving_claimTaxCredits: '',
        part2_line1_itemizedDeductions: '',
        part2_line2_standardDeduction: '',
        part2_line3_subtractLine2FromLine1: '',
        part2_line4_childDeductionAmount: '',
        part2_line5_federalAdjustmentsToIncome: '',
        part2_line6_addLines345: '',
        part2_line7_nonwageIncome: '',
        part2_line8_ncAdditionsToAgi: '',
        part2_line9_addLines78: '',
        part2_line10_subtractLine9FromLine6: '',
        part2_line11_divideLine10By2500: '',
        part2_line12_estimatedNcTaxCredits: '',
        part2_line13_divideLine12By109: '',
        part2_line14_additionalAllowances: '',
        part2_line15_totalAllowancesPartII: '',
        part2_line16_spouseAllowancesToClaim: '',
        part2_line17_finalTotalAllowances: '',
        sch1_homeMortgageInterest: '',
        sch1_realEstatePropertyTaxes: '',
        sch1_charitableContributions: '',
        sch1_medicalAndDentalExpenses: '',
        sch1_repaymentOfClaimOfRightIncome: '',
        sch1_totalEstimatedItemizedDeductions: '',
        sch2_single_income_0_20000: { ...defaultRowData, ...(initialData.sch2_single_income_0_20000 || {}) },
        sch2_single_income_20001_30000: { ...defaultRowData, ...(initialData.sch2_single_income_20001_30000 || {}) },
        sch2_single_income_30001_40000: { ...defaultRowData, ...(initialData.sch2_single_income_30001_40000 || {}) },
        sch2_single_income_40001_50000: { ...defaultRowData, ...(initialData.sch2_single_income_40001_50000 || {}) },
        sch2_single_income_50001_60000: { ...defaultRowData, ...(initialData.sch2_single_income_50001_60000 || {}) },
        sch2_single_income_60001_70000: { ...defaultRowData, ...(initialData.sch2_single_income_60001_70000 || {}) },
        sch2_single_income_over_70000: { ...defaultRowData, ...(initialData.sch2_single_income_over_70000 || {}) },
        sch2_mfj_ss_income_0_40000: { ...defaultRowData, ...(initialData.sch2_mfj_ss_income_0_40000 || {}) },
        sch2_mfj_ss_income_40001_60000: { ...defaultRowData, ...(initialData.sch2_mfj_ss_income_40001_60000 || {}) },
        sch2_mfj_ss_income_60001_80000: { ...defaultRowData, ...(initialData.sch2_mfj_ss_income_60001_80000 || {}) },
        sch2_mfj_ss_income_80001_100000: { ...defaultRowData, ...(initialData.sch2_mfj_ss_income_80001_100000 || {}) },
        sch2_mfj_ss_income_100001_120000: {
          ...defaultRowData,
          ...(initialData.sch2_mfj_ss_income_100001_120000 || {}),
        },
        sch2_mfj_ss_income_120001_140000: {
          ...defaultRowData,
          ...(initialData.sch2_mfj_ss_income_120001_140000 || {}),
        },
        sch2_mfj_ss_income_over_140000: { ...defaultRowData, ...(initialData.sch2_mfj_ss_income_over_140000 || {}) },
        sch2_hoh_income_0_30000: { ...defaultRowData, ...(initialData.sch2_hoh_income_0_30000 || {}) },
        sch2_hoh_income_30001_45000: { ...defaultRowData, ...(initialData.sch2_hoh_income_30001_45000 || {}) },
        sch2_hoh_income_45001_60000: { ...defaultRowData, ...(initialData.sch2_hoh_income_45001_60000 || {}) },
        sch2_hoh_income_60001_75000: { ...defaultRowData, ...(initialData.sch2_hoh_income_60001_75000 || {}) },
        sch2_hoh_income_75001_90000: { ...defaultRowData, ...(initialData.sch2_hoh_income_75001_90000 || {}) },
        sch2_hoh_income_90001_105000: { ...defaultRowData, ...(initialData.sch2_hoh_income_90001_105000 || {}) },
        sch2_hoh_income_over_105000: { ...defaultRowData, ...(initialData.sch2_hoh_income_over_105000 || {}) },
        sch2_mfs_income_0_20000: { ...defaultRowData, ...(initialData.sch2_mfs_income_0_20000 || {}) },
        sch2_mfs_income_20001_30000: { ...defaultRowData, ...(initialData.sch2_mfs_income_20001_30000 || {}) },
        sch2_mfs_income_30001_40000: { ...defaultRowData, ...(initialData.sch2_mfs_income_30001_40000 || {}) },
        sch2_mfs_income_40001_50000: { ...defaultRowData, ...(initialData.sch2_mfs_income_40001_50000 || {}) },
        sch2_mfs_income_50001_60000: { ...defaultRowData, ...(initialData.sch2_mfs_income_50001_60000 || {}) },
        sch2_mfs_income_60001_70000: { ...defaultRowData, ...(initialData.sch2_mfs_income_60001_70000 || {}) },
        sch2_mfs_income_over_70000: { ...defaultRowData, ...(initialData.sch2_mfs_income_over_70000 || {}) },
        sch2_totalEstimatedChildDeduction: '',
        ...initialData,
      });
      listStates();
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    const body = {
      type: 'NC4',
      userId: currentUser?.userId || '',
      userName: currentUser?.userName || '',
      userToken: '',
      pdf: '',
      pdfUrl: 'https://arb-systems-s3-public.s3.us-east-2.amazonaws.com/public/nc-state-w4.pdf',
      pdfSigned: signature,
      signature: signature,
      options: { ...formData, signatureDate: moment().format('YYYY-MM-DD') },
    };

    try {
      await HireDocuments('').create(body);
      toast.success(`Saved successfully!`);
      onSubmitForm(formData);
    } catch (e: any) {
      console.log(e);
      const message = String(e?.response?.data?.message || 'Create error!');
      toast.error(message);
    }

    setLoading(false);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = event.target;
    const inputValue = type === 'checkbox' ? (event.target as HTMLInputElement).checked : value;
    setFormData((prev) => ({ ...prev, [name]: inputValue }));
  };

  const handleNumericInputChange = (name: string, subFieldName?: keyof Schedule2RowDataInternal) => (values: any) => {
    const newFormData = JSON.parse(JSON.stringify(formData));
    if (subFieldName) {
      const newData = {
        ...newFormData,
        [name]: {
          ...(newFormData[name] as Schedule2RowDataInternal),
          [subFieldName]: values.value,
        },
      };
      setFormData(newData);
    } else {
      setFormData({ ...newFormData, [name]: values.value });
    }
  };

  const handleRadioChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const calculateTotalAllowances = () => {
    let calculatedAllowances = '0';
    const getStopHereAllowanceValue = (): string | null => {
      if (formData.filingStatus === 'single_married_separately') {
        const singleAllNo =
          formData.single_itemizedDeductionsExceed === 'no' &&
          formData.single_childDeductionExceed === 'no' &&
          formData.single_federalAdjustments === 'no' &&
          formData.single_claimTaxCredits === 'no';
        const separateAllNo =
          formData.separate_itemizedDeductionsExceed === 'no' &&
          formData.separate_childDeductionExceed === 'no' &&
          formData.separate_federalAdjustments === 'no' &&
          formData.separate_claimTaxCredits === 'no';
        if (singleAllNo || separateAllNo) return '0';
        return null;
      }
      if (formData.filingStatus === 'married_jointly_surviving_spouse') {
        const jointAllNo =
          formData.joint_itemizedDeductionsExceed === 'no' &&
          formData.joint_childDeductionExceed === 'no' &&
          formData.joint_federalAdjustments === 'no' &&
          formData.joint_claimTaxCredits === 'no' &&
          formData.joint_spouseBenefits === 'no';
        const survivingSpouseAllNoForMFJPath =
          formData.surviving_itemizedDeductionsExceed === 'no' &&
          formData.surviving_childDeductionExceed === 'no' &&
          formData.surviving_federalAdjustments === 'no' &&
          formData.surviving_claimTaxCredits === 'no';
        if (jointAllNo) return '0';
        if (survivingSpouseAllNoForMFJPath) return '5';
        return null;
      }
      if (formData.filingStatus === 'head_of_household') {
        const hohAllNo =
          formData.hoh_itemizedDeductionsExceed === 'no' &&
          formData.hoh_childDeductionExceed === 'no' &&
          formData.hoh_federalAdjustments === 'no' &&
          formData.hoh_claimTaxCredits === 'no';
        if (hohAllNo) return '0';
        return null;
      }
      const isAttemptingSurvivingSpouseWorksheet =
        formData.surviving_itemizedDeductionsExceed ||
        formData.surviving_childDeductionExceed ||
        formData.surviving_federalAdjustments ||
        formData.surviving_claimTaxCredits;
      if (isAttemptingSurvivingSpouseWorksheet) {
        const survivingSpouseWorksheetAllNo =
          formData.surviving_itemizedDeductionsExceed === 'no' &&
          formData.surviving_childDeductionExceed === 'no' &&
          formData.surviving_federalAdjustments === 'no' &&
          formData.surviving_claimTaxCredits === 'no';
        if (survivingSpouseWorksheetAllNo) return '5';
      }
      return null;
    };
    const stopHereValue = getStopHereAllowanceValue();
    if (stopHereValue !== null) {
      calculatedAllowances = stopHereValue;
    } else {
      const line17Value = parseInt(formData.part2_line17_finalTotalAllowances, 10);
      calculatedAllowances = !isNaN(line17Value) ? formData.part2_line17_finalTotalAllowances : '0';
    }
    setFormData((prev) => ({ ...prev, totalAllowances: calculatedAllowances }));
  };

  const validateStepOne = (): boolean => {
    const {
      socialSecurityNumber,
      filingStatus,
      firstName,
      lastName,
      address,
      city,
      state: formState,
      zipCode,
    } = formData;
    if (
      !socialSecurityNumber ||
      !filingStatus ||
      !firstName ||
      !lastName ||
      !address ||
      !city ||
      !formState ||
      !zipCode
    ) {
      toast.warn('Please fill in all required fields.');
      return false;
    }
    if (!signature) {
      toast.warn('Employee signature is required.');
      return false;
    }
    return true;
  };

  const nextStep = () => {
    if (currentStep === 1 && !validateStepOne()) {
      return;
    }
    if (currentStep === 2) {
      calculateTotalAllowances();
    }
    if (currentStep === 3) {
      let totalSch2Deduction = 0;
      Object.keys(formData).forEach((key) => {
        if (key.startsWith('sch2_') && key.endsWith('_income') && formData[key] && formData[key].estimatedDeduction) {
          const deduction = parseFloat(formData[key].estimatedDeduction);
          if (!isNaN(deduction)) {
            totalSch2Deduction += deduction;
          }
        } else if (key === 'sch2_totalEstimatedChildDeduction' && formData[key]) {
          const deduction = parseFloat(formData[key]);
          if (!isNaN(deduction)) {
            totalSch2Deduction = deduction;
          }
        }
      });

      setFormData((prev) => ({
        ...prev,
        part2_line1_itemizedDeductions: prev.sch1_totalEstimatedItemizedDeductions,
        part2_line4_childDeductionAmount: totalSch2Deduction.toFixed(2),
        sch2_totalEstimatedChildDeduction: totalSch2Deduction.toFixed(2),
      }));
    }
    if (currentStep === 4) {
      calculateTotalAllowances();
    }
    setCurrentStep((prev) => Math.min(prev + 1, 4));
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleActualFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    calculateTotalAllowances();
    try {
      await onSubmitForm(formData);
      toast.success('NC-4 Form submitted successfully!');
      toggle();
    } catch (error) {
      toast.error('Failed to submit NC-4 form.');
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <NC4StepOne
            formData={formData}
            handleInputChange={handleInputChange}
            handleNumericInputChange={handleNumericInputChange}
            handleRadioChange={handleRadioChange}
            setSignature={setSignature}
            signature={signature}
            states={states}
            loadingStates={loadingStates}
          />
        );
      case 2:
        return <NC4StepTwo formData={formData} handleRadioChange={handleRadioChange} />;
      case 3:
        return (
          <NC4StepThree
            formData={formData}
            handleNumericInputChange={handleNumericInputChange}
            handleRadioChange={handleRadioChange}
          />
        );
      case 4:
        return <NC4StepFour formData={formData} handleNumericInputChange={handleNumericInputChange} />;
      default:
        return <p>Invalid step.</p>;
    }
  };

  const handleCancel = () => {
    toggle();
  };

  return (
    <Modal className="modal-dialog-centered" size="xl" isOpen={isOpen} toggle={handleCancel} backdrop="static">
      <ModalHeader toggle={handleCancel}>
        NC-4 Employee's Withholding Allowance Certificate
        <small className="d-block text-muted">Step {currentStep} of 4</small>
      </ModalHeader>
      <ModalBody>
        <Form onSubmit={handleActualFormSubmit} id="nc4-modal-internal-form">
          {renderStep()}
          <button type="submit" style={{ display: 'none' }} aria-hidden="true"></button>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={prevStep} disabled={currentStep === 1 || isSubmitting}>
          Back
        </Button>
        {currentStep < 4 ? (
          <Button color="primary" onClick={nextStep} disabled={isSubmitting}>
            Next
          </Button>
        ) : (
          <Button color="success" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? <Spinner size="sm" /> : 'Submit'}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default NC4FormModal;
